import {
  DISPUTE_VENUE,
  GOVERNING_LAW,
  LEGAL_ADDRESS,
  LEGAL_EMAIL,
  LEGAL_NAME,
  LEGAL_PHONE,
  LIABILITY_CAP_MONTHS,
  type LegalSection,
} from './business';

export const TERMS_INTRO =
  `These Terms of Use ("Terms") govern your access to and use of the ${LEGAL_NAME} website, mobile experiences, customer account portal, online booking system, digital wallet, and salon services (collectively, the "Services"). By creating an account, booking an appointment, making a payment, or otherwise using the Services, you agree to these Terms and to our Privacy Policy. If you do not agree, do not use the Services.`;

export const TERMS_IMPORTANT_NOTICE = [
  'These Terms may be updated at any time by posting a revised version on our website. Your continued use of the Services after changes are posted constitutes acceptance. It is your responsibility to review these Terms periodically.',
  'Policies displayed during booking (including cancellation and lateness information) are incorporated by reference. If there is any conflict between those display policies and these Terms, these Terms control unless we expressly state otherwise in writing.',
  'These Terms are provided for operational purposes and do not constitute legal advice. You should have qualified counsel review them before relying on them in any formal proceeding.',
];

export const termsSections: LegalSection[] = [
  {
    title: 'Agreement to Terms',
    content: [
      `By accessing or using ${LEGAL_NAME}'s Services, you enter a binding agreement with us. This includes browsing our website, registering for an account, booking appointments, paying online, using your wallet, and visiting our salon.`,
      'Completing a booking or payment constitutes additional acceptance of the policies shown at checkout, including cancellation and important service information.',
      'We may modify these Terms at any time. Material changes will be posted on this page with an updated "Last Updated" date. Continued use after changes means you accept the revised Terms.',
    ],
  },
  {
    title: 'Eligibility',
    content: [
      'You must be at least 18 years old to create an account and use the Services independently. If you book services for a minor, you represent that you are the parent or legal guardian and consent on their behalf.',
      'You agree to provide accurate, current, and complete registration information and to keep it updated.',
      'Each person may maintain only one customer account unless we authorize otherwise in writing.',
    ],
  },
  {
    title: 'Our Services',
    content: [
      `${LEGAL_NAME} provides professional hair styling and related salon services, including but not limited to twists, washing and conditioning, scalp treatments, beading, and children's hairstyles, as listed on our website and in our salon.`,
      'Through our platform you may: browse services and pricing; book standard time-slotted appointments; submit custom booking requests for admin approval; pay online; manage appointments; use a digital wallet; receive notifications; and view hair service records where applicable.',
      `${LEGAL_NAME} is not a medical provider. Our Services are cosmetic and hair-care related only. You are responsible for disclosing allergies, sensitivities, scalp conditions, or other health concerns before treatment.`,
    ],
  },
  {
    title: 'Account Registration and Security',
    content: [
      'Accounts may be created using passwordless email verification (one-time codes), Google sign-in, Apple sign-in, or other methods we support from time to time.',
      'You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account. Notify us immediately at ' +
        LEGAL_EMAIL +
        ' if you suspect unauthorized access.',
      'We may require additional verification for sensitive actions such as withdrawals, large payments, or account recovery.',
    ],
  },
  {
    title: 'Appointment Booking',
    content: [
      'Appointments must be scheduled through our platform or as we otherwise authorize. Available time slots, staff, services, and home-service options are subject to availability and may change without notice.',
      'Standard bookings require a selected date and time. Custom bookings may be submitted without a final schedule; they remain pending until we approve date, time, and price.',
      'We assign stylists based on availability and service requirements. We may change assigned staff without prior notice where operationally necessary.',
      `We reserve the right, at our sole discretion, to decline, reschedule, shorten, or cancel any appointment before or during service for reasons including but not limited to: operational needs; staff availability; safety; suspected fraud; non-payment; policy violations; or conduct we deem inappropriate. Refunds or credits for such actions are entirely at our discretion unless required by applicable law.`,
    ],
  },
  {
    title: 'Customer Conduct and Prohibited Use',
    content: [
      'You agree to treat our staff, other customers, and property with respect. Harassment, threats, discrimination, or abusive behavior may result in immediate refusal of service and account termination.',
      'You must not: provide false or misleading information; attempt to circumvent payments or penalties; initiate fraudulent chargebacks; scrape or reverse-engineer our platform; interfere with system security; use another person\'s account without permission; or use the Services for any unlawful purpose.',
      'Violation of these rules may result in immediate suspension or termination of your account, cancellation of appointments, and pursuit of amounts owed including penalties and collection costs where permitted by law.',
    ],
  },
  {
    title: 'Cancellations, Rescheduling, Lateness, and No-Shows',
    content: [
      'When cancelling through the app, you must provide a cancellation reason. Cancellations are subject to the policies displayed at booking and to these Terms.',
      'We encourage advance notice (for example, at least 48 hours before your appointment) as shown in our booking policies. Display policies are guidelines; enforced fees may differ as described below and in your booking confirmation.',
      'For appointments paid online (card or transfer), automated penalties may apply when enabled in our system, including: a late-cancellation fee (commonly 50% of the appointment total if cancelled within 24 hours of the scheduled time); and a no-show fee (commonly 100% of the appointment total if you fail to attend without valid cancellation).',
      'Lateness may result in shortened service, rescheduling, refusal of service, or additional charges. Examples published in our Important Info may include fines for lateness over 15 minutes (e.g. ₦3,000), charges for extended lateness, and impromptu cancellation fees (e.g. ₦5,000). We may apply such charges via wallet debit, card charge, or invoice.',
      'Penalties may be debited from your wallet even if that results in a negative balance. You remain responsible for settling any negative balance before future bookings or withdrawals.',
    ],
  },
  {
    title: 'Pricing, Fees, and Changes',
    content: [
      'Prices, service descriptions, and durations shown online are indicative and may not include all applicable taxes, platform fees, deposits, home-service surcharges, or custom adjustments.',
      'Final price is confirmed at booking or when we approve a custom booking. Value-added tax (VAT) and other fees may apply as displayed at checkout.',
      `We may change service prices, fees, penalties, surcharges, minimum deposits, and payment requirements at any time without prior notice. Changes apply to new bookings and, where permitted, to existing unpaid balances. Paying or booking after a change constitutes acceptance.`,
    ],
  },
  {
    title: 'Payments',
    content: [
      'Online card payments are processed in Nigerian Naira (NGN) through Paystack or other payment partners we designate. Payment sessions may expire after a limited time (for example, 30 minutes); failed or abandoned payments do not confirm your appointment.',
      'You may apply wallet balance toward eligible payments. If your wallet covers the full amount, no card redirect may be required.',
      'You agree not to abuse chargebacks or payment disputes. Raise billing concerns with us first at ' +
        LEGAL_EMAIL +
        ' before disputing with your bank. Fraudulent chargebacks may result in account termination and recovery action.',
    ],
  },
  {
    title: 'Deposits, Upfront Payment, and Escrow',
    content: [
      'Certain services may require a deposit or full upfront payment before confirmation. Requirements may vary by service and by our settings.',
      'Where upfront payment applies and service is not yet completed, funds may be held in escrow and released when the appointment is marked completed, subject to our internal processes.',
      'If the final approved price differs from what you paid online, we may credit or debit your wallet for the difference. You authorize such adjustments when booking and paying through the platform.',
    ],
  },
  {
    title: 'Wallet',
    content: [
      'Your digital wallet is a stored-value ledger for use within our platform only. It is not a bank account, does not earn interest, and is not insured as a deposit.',
      'Wallet transactions include credits (such as overpayment refunds or admin adjustments) and debits (such as payments, penalties, levies, and withdrawals). All entries are recorded in your transaction history.',
      'We may permit negative wallet balances when applying penalties or fees. You must settle negative balances before making new bookings or withdrawal requests. We may offset future credits against outstanding debits.',
    ],
  },
  {
    title: 'Withdrawals',
    content: [
      'You may request withdrawal of available wallet balance to a Nigerian bank account you provide. We collect bank name, account number, and account name for this purpose.',
      'Withdrawals are subject to minimum amounts (for example, ₦1,000), admin review, and only one pending request at a time. We may approve, reject, or delay payouts for fraud prevention, compliance, or incorrect bank details.',
      'You may cancel a pending withdrawal request before it is processed. Approved withdrawals debit your wallet; rejected requests do not.',
    ],
  },
  {
    title: 'Refunds',
    content: [
      'Unless we expressly agree otherwise in writing, refunds are not automatic upon cancellation. Cancellation does not guarantee a refund of amounts paid.',
      'Refunds, when granted, may be issued to your wallet, to your original payment method, or by another means we determine, subject to our refund settings and applicable law.',
      'Refund requests may be subject to time limits, processing periods, and administrative review. Chargebacks without contacting us first may be treated as a serious breach of these Terms.',
    ],
  },
  {
    title: 'Hair Records and Appointment Media',
    content: [
      'Our stylists may create hair profile records (such as texture, porosity, density, and scalp notes) and upload before/after images linked to your appointments to deliver and improve services.',
      'You grant us a license to store, use, and display such records internally for service delivery, training, and quality assurance. We do not use them as medical diagnoses.',
      'You may request correction of inaccurate profile data through your account or by contacting us. Deletion may be limited where records are needed for legal, tax, or dispute purposes.',
    ],
  },
  {
    title: 'Communications',
    content: [
      'By using the Services, you consent to receive transactional communications via email, SMS, push notifications (including device tokens registered to your account), and in-app messages about appointments, payments, wallet activity, and account status.',
      'Service-related messages may be sent without separate marketing consent where permitted by law. Promotional messages, if any, will comply with applicable opt-out requirements.',
    ],
  },
  {
    title: 'Intellectual Property',
    content: [
      `All website content, logos, branding, text, images, and software are owned by ${LEGAL_NAME} or its licensors and protected by applicable intellectual property laws.`,
      'You receive a limited, non-exclusive, non-transferable license to access the Services for personal, non-commercial use. You may not copy, modify, distribute, or exploit our content without written permission.',
    ],
  },
  {
    title: 'Disclaimers',
    content: [
      'Services are provided "as is" and "as available" to the fullest extent permitted by law. We do not guarantee specific aesthetic outcomes, durability of styles, or compatibility with all hair types.',
      'You are responsible for disclosing allergies, sensitivities, prior reactions, and relevant conditions before treatment. Failure to disclose may affect results and our liability.',
      'To the maximum extent permitted under Nigerian law, we disclaim liability for indirect, incidental, special, consequential, or punitive damages arising from your use of the Services.',
    ],
  },
  {
    title: 'Limitation of Liability',
    content: [
      `Our total aggregate liability to you for any claim arising from or related to the Services shall not exceed the greater of: (a) the fees you paid to us for the specific appointment giving rise to the claim; or (b) the total fees you paid to us in the ${LIABILITY_CAP_MONTHS} months immediately preceding the claim.`,
      'Nothing in these Terms excludes or limits liability that cannot be excluded under applicable law, including liability for death or personal injury caused by our gross negligence or willful misconduct where such limitation is prohibited.',
    ],
  },
  {
    title: 'Indemnification',
    content: [
      `You agree to indemnify, defend, and hold harmless ${LEGAL_NAME}, its owners, employees, and agents from claims, damages, losses, and expenses (including reasonable legal fees) arising from: your breach of these Terms; your misuse of the Services; your conduct at the salon; false information you provide; or your violation of any law or third-party rights.`,
    ],
  },
  {
    title: 'Suspension and Termination',
    content: [
      'We may suspend, restrict, or terminate your account at any time, with or without notice, for violation of these Terms, non-payment, fraud risk, abusive conduct, or any reason we deem necessary to protect our business, staff, or customers.',
      'Upon termination, your right to use the Services ends immediately. Outstanding appointment charges, penalties, and negative wallet balances remain due. Provisions that by nature should survive (including payment obligations, disclaimers, limitation of liability, indemnity, and dispute resolution) survive termination.',
    ],
  },
  {
    title: 'Dispute Resolution',
    content: [
      'Before initiating formal proceedings, you agree to contact us at ' +
        LEGAL_EMAIL +
        ' and attempt good-faith resolution for at least 14 days.',
      `These Terms are governed by the laws of the ${GOVERNING_LAW}. You agree that courts located in ${DISPUTE_VENUE} shall have exclusive jurisdiction over disputes not resolved informally, subject to mandatory consumer protections under applicable law.`,
    ],
  },
  {
    title: 'Force Majeure',
    content: [
      'We are not liable for failure or delay in performing obligations due to events beyond our reasonable control, including natural disasters, power or internet outages, government actions, labor disputes, pandemics, or failures of payment processors and other third-party services.',
    ],
  },
  {
    title: 'General Provisions',
    content: [
      'These Terms, together with the Privacy Policy and policies incorporated by reference, constitute the entire agreement between you and us regarding the Services.',
      'If any provision is held invalid, the remaining provisions remain in effect. Our failure to enforce a right is not a waiver of that right.',
      'We may assign our rights and obligations under these Terms without your consent. You may not assign your rights without our written consent.',
      'For questions about these Terms, contact:',
      LEGAL_NAME,
      LEGAL_ADDRESS,
      `Phone: ${LEGAL_PHONE}`,
      `Email: ${LEGAL_EMAIL}`,
    ],
  },
];
